/**
 * Declarative Soma configuration ("Config as Code").
 *
 * These types and the `defineSomaConfig` helper let client projects declare
 * their organization's skills and agents in a type-safe, git-tracked file
 * (e.g. `soma.config.ts`) that the `zea soma sync` command compiles and sends
 * to the Soma backend for idempotent upsert.
 */

export interface SkillSpec {
  /** Unique skill name (e.g. `fund-management`). */
  name: string
  /** Human-readable description of what the skill does. */
  description?: string
  /** Path to the skill definition file (e.g. `./skills/fund-management/SKILL.md`). */
  filePath?: string
  /** Inline skill content. Mutually exclusive with `filePath` in practice. */
  content?: string
}

export interface AgentSpec {
  /** Unique agent id (e.g. `fund-management-copilot`). */
  id: string
  /** Display name for the agent. */
  name: string
  /** Engine that runs the agent. */
  engine?: 'pi' | 'react' | 'opencode'
  /** Model backing the agent. Known values are suggested for autocomplete; any string is accepted. */
  model?: 'deepseek-v4-pro' | 'gpt-4o' | 'claude-sonnet-4-20250514' | (string & {})
  /** System prompt that primes the agent's behavior. */
  systemPrompt?: string
  /** Names of skills assigned to this agent. */
  skills?: string[]
}

export interface SomaConfig {
  /** Organization slug (e.g. `zea`). */
  organization: string
  skills?: SkillSpec[]
  agents?: AgentSpec[]
}

function isNonEmptyString(value: unknown, label: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`[defineSomaConfig] "${label}" must be a non-empty string.`)
  }
  return value
}

function assertOptionalStringArray(value: unknown, label: string): void {
  if (value === undefined) return
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) {
    throw new Error(`[defineSomaConfig] "${label}" must be an array of strings when provided.`)
  }
}

/**
 * Validates a declarative Soma configuration and returns it unchanged.
 *
 * The validation is intentionally lightweight and dependency-free: it asserts
 * the required fields (`organization`, and `name`/`id` on each skill/agent)
 * so invalid configs fail fast at sync time instead of silently producing
 * a partial upsert on the backend.
 */
export function defineSomaConfig(config: SomaConfig): SomaConfig {
  if (config === null || typeof config !== 'object') {
    throw new Error('[defineSomaConfig] config must be an object.')
  }

  isNonEmptyString(config.organization, 'organization')

  if (config.skills !== undefined) {
    if (!Array.isArray(config.skills)) {
      throw new Error('[defineSomaConfig] "skills" must be an array when provided.')
    }
    config.skills.forEach((skill, index) => {
      if (skill === null || typeof skill !== 'object') {
        throw new Error(`[defineSomaConfig] skills[${index}] must be an object.`)
      }
      isNonEmptyString(skill.name, `skills[${index}].name`)
    })
  }

  if (config.agents !== undefined) {
    if (!Array.isArray(config.agents)) {
      throw new Error('[defineSomaConfig] "agents" must be an array when provided.')
    }
    config.agents.forEach((agent, index) => {
      if (agent === null || typeof agent !== 'object') {
        throw new Error(`[defineSomaConfig] agents[${index}] must be an object.`)
      }
      isNonEmptyString(agent.id, `agents[${index}].id`)
      isNonEmptyString(agent.name, `agents[${index}].name`)
      assertOptionalStringArray(agent.skills, `agents[${index}].skills`)
    })
  }

  return config
}
